import { useMemo } from "react"
import { BigNumber } from "ethers"
import { useQueries } from "@tanstack/react-query"
import { MiniChef } from "types/ethers-contracts/MiniChef"
import { FerroFarmV2 } from "types/ethers-contracts/FerroFarmV2"
import { useMiniChefContract, useFerroFarmV2Contract } from "../useContract"
import { useActiveWeb3React } from ".."

import { getChainId } from "src/utils"
import { POOLS_MAP } from "src/constants"
import { BIG_ZERO } from "src/constants/bigNumber"
import { FETCH_USER_DATA } from "src/constants/queryKeys"

async function fetchUserStakeData(
  poolPid: number | null,
  account: string | null | undefined,
  rewardContract: MiniChef | FerroFarmV2 | null,
) {
  const amountStakedInRewards =
    poolPid !== null && account && rewardContract
      ? (await rewardContract.userInfo(poolPid, account)).amount
      : BIG_ZERO
  return amountStakedInRewards
}

export default function useTotalStake(): BigNumber {
  const chainId = getChainId()
  const { account } = useActiveWeb3React()
  const pools = useMemo(() => {
    return Object.values(POOLS_MAP).filter(
      ({ addresses }) => chainId && addresses[chainId],
    )
  }, [chainId])
  const farmContract = useMiniChefContract()
  const farmV2Contract = useFerroFarmV2Contract()

  const userStakeResults = useQueries({
    queries: pools.map((pool) => {
      const rewardsContract = pool.version === 2 ? farmV2Contract : farmContract
      const poolPid = pool.rewardPids[chainId]
      return {
        queryKey: [FETCH_USER_DATA, "Stake", pool.name, account],
        queryFn: () => fetchUserStakeData(poolPid, account, rewardsContract),
        enabled: !!(account && rewardsContract && poolPid),
      }
    }),
  })
  const totalStake = useMemo(
    () =>
      userStakeResults
        .map(({ data: stake }) => {
          return stake ?? BIG_ZERO
        })
        .reduce((acc, cur) => {
          return acc.add(cur)
        }, BIG_ZERO),
    [userStakeResults],
  )
  return totalStake
}
